import { graphql } from 'react-relay';
import { useCallback } from 'react';
import type { ExecutionResult } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';

import type {
	TreeDestinationPosition,
	TreeSourcePosition,
} from '@confluence/tree/entry-points/tree-types.ts';

import type {
	useMovePageHandlerMovePageTopLevelMutation as useMovePageHandlerMovePageTopLevelMutation$data,
	useMovePageHandlerMovePageTopLevelMutationVariables as useMovePageHandlerMovePageTopLevelMutation$variables,
} from './__types__/useMovePageHandlerMovePageTopLevelMutation.ts';
import type {
	useMovePageHandlerMovePageAppendMutation as useMovePageHandlerMovePageAppendMutation$data,
	useMovePageHandlerMovePageAppendMutationVariables as useMovePageHandlerMovePageAppendMutation$variables,
} from './__types__/useMovePageHandlerMovePageAppendMutation.ts';
import type {
	useMovePageHandlerMovePageBeforeMutation as useMovePageHandlerMovePageBeforeMutation$data,
	useMovePageHandlerMovePageBeforeMutationVariables as useMovePageHandlerMovePageBeforeMutation$variables,
} from './__types__/useMovePageHandlerMovePageBeforeMutation.ts';
import type {
	useMovePageHandlerMovePageAfterMutation as useMovePageHandlerMovePageAfterMutation$data,
	useMovePageHandlerMovePageAfterMutationVariables as useMovePageHandlerMovePageAfterMutation$variables,
} from './__types__/useMovePageHandlerMovePageAfterMutation.ts';
import type { ContentTreeItem } from './data-transformers.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
export type MovePageToNewParentParams = {
	pageId: string;
	parentId: string;
	previousParentPageId?: string;
};
export type MovePageAlongsideSiblingParams = {
	pageId: string;
	siblingId: string;
	positionToSibling: 'before' | 'after';
	previousParentPageId?: string;
};
export type MovePageTopLevelParams = {
	pageId: string;
	spaceKey: string;
	previousParentPageId?: string;
};
export type MovePageParams =
	| MovePageToNewParentParams
	| MovePageAlongsideSiblingParams
	| MovePageTopLevelParams;
export type MovePageAdditionalParams = {
	contentStatus: string | null;
	destination: TreeDestinationPosition;
	movedPageTitle: string;
	source: TreeSourcePosition;
	sourceParent: ContentTreeItem;
	spaceId?: string | null;
	revertMove: () => MovePageParams;
};
export type OverrideMovePageParams = {
	newParentPageId?: string;
	newSpaceKey: string;
	targetId?: string;
	position?: string;
	closeMove?: () => void;
	previousParentPageId?: string;
};
export type UnifiedMutationResult = {
	mutationResult:
		| {
				page:
					| {
							id: string | null | undefined;
							links:
								| {
										webui: string | null | undefined;
										editui: string | null | undefined;
								  }
								| null
								| undefined;
					  }
					| null
					| undefined;
		  }
		| null
		| undefined;
};
export type MovePageHandler = (
	params: MovePageParams,
) => Promise<ExecutionResult<UnifiedMutationResult>>;
export const useMovePageHandler = (): {
	movePage: MovePageHandler;
} => {
	const [movePageAfter] = useMutationInterop<
		useMovePageHandlerMovePageAfterMutation$data,
		useMovePageHandlerMovePageAfterMutation$variables
	>(graphql`
		mutation useMovePageHandlerMovePageAfterMutation($pageId: ID!, $siblingId: ID!) {
			movePageAfter(input: { pageId: $pageId, siblingId: $siblingId }) {
				page {
					id
					links {
						webui
						editui
					}
				}
			}
		}
	`);
	const [movePageBefore] = useMutationInterop<
		useMovePageHandlerMovePageBeforeMutation$data,
		useMovePageHandlerMovePageBeforeMutation$variables
	>(graphql`
		mutation useMovePageHandlerMovePageBeforeMutation($pageId: ID!, $siblingId: ID!) {
			movePageBefore(input: { pageId: $pageId, siblingId: $siblingId }) {
				page {
					id
					links {
						webui
						editui
					}
				}
			}
		}
	`);
	const [movePageAppend] = useMutationInterop<
		useMovePageHandlerMovePageAppendMutation$data,
		useMovePageHandlerMovePageAppendMutation$variables
	>(graphql`
		mutation useMovePageHandlerMovePageAppendMutation($pageId: ID!, $parentId: ID!) {
			movePageAppend(input: { pageId: $pageId, parentId: $parentId }) {
				page {
					id
					links {
						webui
						editui
					}
				}
			}
		}
	`);
	const [movePageTopLevel] = useMutationInterop<
		useMovePageHandlerMovePageTopLevelMutation$data,
		useMovePageHandlerMovePageTopLevelMutation$variables
	>(graphql`
		mutation useMovePageHandlerMovePageTopLevelMutation($pageId: ID!, $spaceKey: String!) {
			movePageTopLevel(input: { pageId: $pageId, targetSpaceKey: $spaceKey }) {
				page {
					id
					links {
						webui
						editui
					}
				}
			}
		}
	`);
	const movePage = useCallback<MovePageHandler>(
		async (params) => {
			const thenFn =
				<T>(resultFormatter: (data: T) => UnifiedMutationResult['mutationResult']) =>
				({ data, ...rest }: ExecutionResult<T>): ExecutionResult<UnifiedMutationResult> => {
					return {
						...rest,
						data:
							typeof data === 'undefined'
								? undefined
								: {
										mutationResult: resultFormatter(data),
									},
					};
				};
			if ('parentId' in params) {
				return movePageAppend({
					variables: { pageId: params.pageId, parentId: params.parentId },
				}).then(thenFn((data) => data.movePageAppend));
			} else if ('spaceKey' in params) {
				return movePageTopLevel({
					variables: { pageId: params.pageId, spaceKey: params.spaceKey },
				}).then(thenFn((data) => data.movePageTopLevel));
			} else if (params.positionToSibling === 'after') {
				return movePageAfter({
					variables: { pageId: params.pageId, siblingId: params.siblingId },
				}).then(thenFn((data) => data.movePageAfter));
			} else {
				return movePageBefore({
					variables: { pageId: params.pageId, siblingId: params.siblingId },
				}).then(thenFn((data) => data.movePageBefore));
			}
		},
		[movePageAfter, movePageAppend, movePageBefore, movePageTopLevel],
	);
	return { movePage };
};
